import { useEffect, useMemo, useState } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { Box, Typography, IconButton, Button, Grid, Menu, MenuItem, Divider, ListItemText } from "@mui/material";
import {
  DoneOutline,
  QueryBuilder,
  KeyboardArrowRight,
} from "@mui/icons-material";
import {
  IconGeneralfdbk,
  IconAssigneeHlpdsk,
  Spanner,
  IconSuggestion,
  IconContentConcern,
  IconSupport,
} from "Icons";
import ViewDetailsModal from "./ViewDetailsModal";
import { Breadcrumb } from "components";
import TicketSearch from "components/TicketSearch/TicketSearch";
import { withUserContext, withApiContext } from "context";

const theme = createTheme({
  typography: {
    fontFamily: "Poppins, sans-serif",
    allVariants: {
      fontFamily: "Poppins, sans-serif",
    },
  },
});

const renderCard = (ticket, handleOpenModal, getRequestIcon) => (
  <Box
    sx={{
      border: "1px solid",
      borderColor: "divider",
      p: 2,
      height: 200, // Fixed height
      minHeight: 200, // Ensure minimum height
      maxHeight: 200, // Prevent content from expanding height
      width: "100%", // Full width of the grid item
      boxSizing: "border-box", // Ensure padding and border are included in the width
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-around",
      boxShadow: 2,
      position: "relative", // <-- Add this
      transition: "transform 200ms ease, box-shadow 200ms ease",
      "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: 4,
      },
    }}
  >
    <Box display="flex" justifyContent="space-between" alignItems="center">
      <Box display="flex" alignItems="center">
        {getRequestIcon(ticket.ticket_type)}
        <Typography variant="subtitle2" fontWeight="medium" ml={1} noWrap>
          {ticket.ticket_type
            .replace("_", " ") // Replace underscores with spaces
            .split(" ") // Split the string into an array of words
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize the first letter of each word
            .join(" ")}
        </Typography>
      </Box>
      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="caption" color="textSecondary" noWrap>
          {`#ILETK00${ticket.ticket_id}`}
        </Typography>
        <IconButton
          size="small"
          sx={{ border: 1, borderColor: "divider" }}
          onClick={() => handleOpenModal(ticket)}
        >
          <KeyboardArrowRight fontSize="small" />
        </IconButton>
      </Box>
    </Box>

    <Box flexGrow={1} py={1}>
      <Box display="flex" alignItems="center" gap={1} mb={1.5}>
        <Spanner sx={{ width: 24, height: 24 }} />
        <Typography variant="body2" noWrap>
          {ticket.suggestion_title || ticket.ticket_desc || "No description available"}
        </Typography>
      </Box>

      <Box display="flex" alignItems="center" gap={1} mb={1.5}>
        <IconAssigneeHlpdsk sx={{ width: 24, height: 24 }} />
        <Typography variant="body2" noWrap>
          {ticket.member_name || "Unassigned"}
        </Typography>
      </Box>

      <Box
        display="flex"
        alignItems="center"
        gap={1}
        sx={{
          position: "absolute",
          bottom: 16,
          right: 16,
          maxHeight: { xs: 28, sm: 27, md: 28 },
          borderRadius: "10px",
          fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
          bgcolor: ticket.is_completed
            ? "success.main"
            : ticket.is_assigned
            ? "secondary.main"
            : "warning.main",
          textTransform: "none",
          px: 1.5,
          py: 0.5,
          color: "#ffffff",
          width: "fit-content",
        }}
      >
        {ticket.is_assigned ? (
          <IconSupport color="inherit" sx={{ fontSize: 18 }} />
        ) : ticket.is_completed ? (
          <DoneOutline color="inherit" sx={{ fontSize: 18 }} />
        ) : (
          <QueryBuilder color="inherit" sx={{ fontSize: 18 }} />
        )}

        <Typography variant="body2" color="inherit" noWrap>
          {ticket.is_assigned
            ? "Dev Support"
            : ticket.is_completed
            ? "Resolved"
            : "Pending"}
        </Typography>
      </Box>
    </Box>
  </Box>
);

const ViewHelpdesk = ({ ctx_user, ctx_api }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [ticketList, setTicketList] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterAssigned, setFilterAssigned] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [anchorMenu, setAnchorMenu] = useState(null);
  const [anchorType, setAnchorType] = useState(null);
  const [anchorStatus, setAnchorStatus] = useState(null);
  const [anchorAssigned, setAnchorAssigned] = useState(null);
  const [anchorSort, setAnchorSort] = useState(null);

  const { user } = ctx_user;
  const organization_id = user.organizationId;

  const handleSearchResult = (result) => {
    setIsSearching(false);
    setSearchResult(result);
  };

  const clearSearch = () => {
    setSearchResult(null);
  };

  const getRequestIcon = (type) => {
    switch (type) {
      case "general_feedback":
        return <IconGeneralfdbk />;
      case "content_concern":
        return <IconContentConcern />;
      case "suggestions":
        return <IconSuggestion />;
      default:
        return <IconSupport />;
    }
  };

  const handleOpenModal = (ticket) => {
    setSelectedTicket(ticket.ticket_id);
    setIsModalOpen(true);
  };

  const getAllTickets = async () => {
    try {
      const data = await ctx_api.privateRequest({
        cmd: "get_tickets_admin",
        args: { organizationId: organization_id },
      });

      const tickets = data?.rsp[0]?.details?.ticket || [];
      setTicketList(tickets);

      console.log(tickets);
    } catch (error) {}
  };


  useEffect(() => {
    if (user?.organizationId) getAllTickets();
  }, [user]);

  const visibleTickets = useMemo(() => {
    if (!Array.isArray(ticketList)) return [];
    let list = [...ticketList];

    // Type filter
    if (filterType !== "all") {
      list = list.filter((t) => String(t.ticket_type) === filterType);
    }

    // Status filter
    if (filterStatus !== "all") {
      const wantResolved = filterStatus === "resolved";
      list = list.filter((t) => Boolean(t.is_completed) === wantResolved);
    }

    // Assigned filter (assigned to support)
    if (filterAssigned !== "all") {
      const wantAssigned = filterAssigned === "assigned";
      list = list.filter((t) => {
        // prefer explicit flag if present, fallback to presence of member_name
        const isAssigned = typeof t.is_assigned !== "undefined" ? Boolean(t.is_assigned) : Boolean(t.member_name);
        return isAssigned === wantAssigned;
      });
    }

    // Sorting
    list.sort((a, b) => {
      const aDate = a.created_on ? new Date(a.created_on).getTime() : 0;
      const bDate = b.created_on ? new Date(b.created_on).getTime() : 0;
      const aId = Number(a.ticket_id) || 0;
      const bId = Number(b.ticket_id) || 0;
      const aType = String(a.ticket_type || "").localeCompare(String(b.ticket_type || ""));
      const aMember = String(a.member_name || "").localeCompare(String(b.member_name || ""));

      switch (sortBy) {
        case "oldest":
          return aDate - bDate || aId - bId;
        case "ticket":
          return aId - bId;
        case "type":
          return aType;
        case "member":
          return aMember;
        case "newest":
        default:
          return bDate - aDate || bId - aId;
      }
    });

    return list;
  }, [ticketList, filterType, filterStatus, filterAssigned, sortBy]);

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            mb: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Breadcrumb
            list={[
              {
                title: "Help Desk",
                current: true,
              },
            ]}
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {searchResult && (
              <Button
                variant="outlined"
                size="small"
                onClick={clearSearch}
                sx={{ textTransform: "none" }}
              >
                Show All Tickets
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              onClick={(e) => setAnchorMenu(e.currentTarget)}
              sx={{ textTransform: "none" }}
            >
              Filter & Sort
            </Button>
            <TicketSearch onSearchResult={handleSearchResult} ticketList={ticketList} />
          </Box>
        </Box>

        {/* Main menu */}
        <Menu
          anchorEl={anchorMenu}
          open={Boolean(anchorMenu)}
          onClose={() => setAnchorMenu(null)}
        >
          <MenuItem onClick={(e) => setAnchorType(e.currentTarget)}>
            <ListItemText primary="Type" secondary={filterType === "all" ? "All" : filterType.replace("_", " ")} />
          </MenuItem>
          <MenuItem onClick={(e) => setAnchorStatus(e.currentTarget)}>
            <ListItemText primary="Status" secondary={filterStatus === "all" ? "All" : filterStatus} />
          </MenuItem>
          <MenuItem onClick={(e) => setAnchorAssigned(e.currentTarget)}>
            <ListItemText primary="Assigned" secondary={filterAssigned === "all" ? "All" : filterAssigned} />
          </MenuItem>
          <Divider />
          <MenuItem onClick={(e) => setAnchorSort(e.currentTarget)}>
            <ListItemText primary="Sort By" secondary={
              sortBy === "newest" ? "Newest" : sortBy === "oldest" ? "Oldest" : sortBy === "ticket" ? "Ticket ID" : sortBy === "type" ? "Type A–Z" : "Member A–Z"
            } />
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => { setFilterType("all"); setFilterStatus("all"); setFilterAssigned("all"); setSortBy("newest"); }}>
            Reset Filters
          </MenuItem>
        </Menu>

        {/* Type submenu */}
        <Menu anchorEl={anchorType} open={Boolean(anchorType)} onClose={() => setAnchorType(null)}>
          <MenuItem onClick={() => { setFilterType("all"); setAnchorType(null); }}>All</MenuItem>
          <MenuItem onClick={() => { setFilterType("general_feedback"); setAnchorType(null); }}>General Feedback</MenuItem>
          <MenuItem onClick={() => { setFilterType("content_concern"); setAnchorType(null); }}>Content Concern</MenuItem>
          <MenuItem onClick={() => { setFilterType("suggestions"); setAnchorType(null); }}>Suggestions</MenuItem>
        </Menu>

        {/* Status submenu */}
        <Menu anchorEl={anchorStatus} open={Boolean(anchorStatus)} onClose={() => setAnchorStatus(null)}>
          <MenuItem onClick={() => { setFilterStatus("all"); setAnchorStatus(null); }}>All</MenuItem>
          <MenuItem onClick={() => { setFilterStatus("pending"); setAnchorStatus(null); }}>Pending</MenuItem>
          <MenuItem onClick={() => { setFilterStatus("resolved"); setAnchorStatus(null); }}>Resolved</MenuItem>
        </Menu>

        {/* Assigned submenu */}
        <Menu anchorEl={anchorAssigned} open={Boolean(anchorAssigned)} onClose={() => setAnchorAssigned(null)}>
          <MenuItem onClick={() => { setFilterAssigned("all"); setAnchorAssigned(null); }}>All</MenuItem>
          <MenuItem onClick={() => { setFilterAssigned("assigned"); setAnchorAssigned(null); }}>Assigned</MenuItem>
          <MenuItem onClick={() => { setFilterAssigned("unassigned"); setAnchorAssigned(null); }}>Unassigned</MenuItem>
        </Menu>

        {/* Sort submenu */}
        <Menu anchorEl={anchorSort} open={Boolean(anchorSort)} onClose={() => setAnchorSort(null)}>
          <MenuItem onClick={() => { setSortBy("newest"); setAnchorSort(null); }}>Newest</MenuItem>
          <MenuItem onClick={() => { setSortBy("oldest"); setAnchorSort(null); }}>Oldest</MenuItem>
          <MenuItem onClick={() => { setSortBy("ticket"); setAnchorSort(null); }}>Ticket ID</MenuItem>
          <MenuItem onClick={() => { setSortBy("type"); setAnchorSort(null); }}>Type A–Z</MenuItem>
          <MenuItem onClick={() => { setSortBy("member"); setAnchorSort(null); }}>Member A–Z</MenuItem>
        </Menu>

        {isSearching ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography>Searching for tickets...</Typography>
          </Box>
        ) : searchResult !== null ? (
          Array.isArray(searchResult) && searchResult.length > 0 ? (
            <Grid
              container
              spacing={2}
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: "16px", // Adjust gap as needed
              }}
            >
              {searchResult.map((item, index) => (
                <Grid item key={index} xs={12} sm={6} md={4}>
                  {renderCard(item, handleOpenModal, getRequestIcon)}
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography>No tickets found.</Typography>
            </Box>
          )
        ) : (
          <>
          <Grid
            container
            spacing={2}
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "50px", // Adjust gap as needed
            }}
          >
            {visibleTickets && visibleTickets.length > 0 ? (
              visibleTickets.map((ticket, index) => (
                <Grid item key={index} xs={12} sm={6} md={4}>
                  {renderCard(ticket, handleOpenModal, getRequestIcon)}
                </Grid>
              ))
            ) : (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography>No tickets found.</Typography>
              </Box>
            )}
          </Grid>
          </>
        )}

        <ViewDetailsModal
          open={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedTicket(null);
            getAllTickets();
          }}
          ticketId={selectedTicket}
          ctx_api={ctx_api}
          ctx_user={ctx_user}
        />
      </Box>
    </ThemeProvider>
  );
};

export default withUserContext(withApiContext(ViewHelpdesk));
